<script lang="ts">
  // Everything that sits behind one contact: our own pages first, then whatever this
  // company publishes. Drawn as a sheet rather than as tabs on purpose — tabs would put
  // company content at the same level as the app's own navigation, and the whole defence
  // against a page impersonating the app is that the app owns its frame.

  import { cachedViews } from '$lib/db/views';
  import type { ViewRow } from '$lib/db/db';
  import { t } from '$lib/i18n/index.svelte';
  import { live } from '$lib/state/live';

  import { navigate } from '../../app/router.svelte';

  let {
    accountId,
    companyName,
    onclose,
  }: { accountId: string; companyName: string; onclose: () => void } = $props();

  const views = live<ViewRow[]>(() => cachedViews(accountId), []);

  function go(route: () => void): void {
    onclose();
    route();
  }

  function onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      onclose();
    }
  }
</script>

<svelte:window on:keydown={onKey} />

<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_noninteractive_element_interactions -->
<div class="scrim" role="presentation" onclick={onclose}></div>

<div class="sheet" role="dialog" aria-modal="true" aria-label={companyName}>
  <p class="who">{companyName}</p>

  <button class="row" onclick={() => go(() => navigate({ name: 'orders', accountId }))}>
    <span class="glyph" aria-hidden="true">📦</span>{t('orders.title')}
  </button>
  <button class="row" onclick={() => go(() => navigate({ name: 'contact', accountId }))}>
    <span class="glyph" aria-hidden="true">⚙️</span>{t('contact.title')}
  </button>

  {#if $views.length > 0}
    <!-- Attribution, not a warning: it says who wrote what follows, the way a message
         bubble does, and asks the reader to decide nothing. -->
    <p class="from">{t('views.fromSupplier')}</p>
    {#each $views as view (view.id)}
      <button
        class="row"
        onclick={() => go(() => navigate({ name: 'view', accountId, viewId: view.viewId }))}
      >
        <span class="glyph" aria-hidden="true">▸</span>{view.title}
      </button>
    {/each}
  {/if}
</div>

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 35%);
    z-index: 20;
  }
  .sheet {
    position: fixed;
    z-index: 21;
    left: 50%;
    transform: translateX(-50%);
    bottom: 0;
    width: 100%;
    max-width: 600px;
    background: var(--surface);
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    padding: 8px 0 max(16px, env(safe-area-inset-bottom));
    box-shadow: 0 -8px 32px rgb(0 0 0 / 18%);
  }
  .who {
    margin: 8px 20px 12px;
    font-weight: 700;
    font-size: 15px;
  }
  .from {
    margin: 14px 20px 6px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--ink-soft);
    border-top: 1px solid var(--line);
    padding-top: 14px;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    border: 0;
    background: transparent;
    color: var(--ink);
    font: inherit;
    text-align: left;
    padding: 13px 20px;
    cursor: pointer;
  }
  .row:hover,
  .row:focus-visible {
    background: var(--line);
  }
  .glyph {
    width: 22px;
    text-align: center;
  }
</style>
